import { Editor } from '@tiptap/react';
import { IconName, MinimalIconProps } from '@ardoq/icons';
import { ToolbarDropdownOption } from './ToolbarDropdown';
import { DropdownButtonOptionType } from '@ardoq/dropdown-menu-ui';
import { findExtensionByName } from './toolbarUtils';
import { Level } from '@tiptap/extension-heading';
import { PlaceholderExtensionItems } from '../extensions/PlaceholderExtension';
import { handlePresentationSelection } from '../extensions/PresentationExtension';
import { ImageItem } from '../extensions/ImageToolbarButton';
import {
  DEFAULT_IMAGE_SIZE_PRESET,
  isImageSizePreset,
} from '../extensions/imageSizing';

const withDefaultSize = (image: ImageItem): ImageItem => ({
  ...image,
  sizePreset: isImageSizePreset(image.sizePreset)
    ? image.sizePreset
    : DEFAULT_IMAGE_SIZE_PRESET,
});

type ToolbarItemCommon = {
  tooltipText: string;
  icon: MinimalIconProps;
  label: string;
  shortcut?: string[];
};
export type ToolbarItemDropdown = {
  type: 'dropdown';
  getOptions: (editor: Editor) => ToolbarDropdownOption[];
  getActiveOptions?: (editor: Editor) => ToolbarDropdownOption[];
  isDisabled?: (editor: Editor) => boolean;
} & ToolbarItemCommon;

type ToolbarItemLinkDropdown = {
  type: 'link-dropdown';
} & ToolbarItemCommon;

type ToolbarItemAction = {
  type: 'action';
  onClick: (editor: Editor) => any;
} & ToolbarItemCommon;

type ToolbarItemColorPicker = {
  type: 'color-picker';
  resolveCurrentColor: (editor: Editor) => string;
  onClick: (editor: Editor, color: string) => void;
} & ToolbarItemCommon;

export type ToolbarItem =
  | ToolbarItemDropdown
  | ToolbarItemColorPicker
  | ToolbarItemLinkDropdown
  | ToolbarItemAction;

export type ToolbarItemLabels = {
  textStyles: string;
  normal: string;
  heading: (level: number) => string;
  bold: string;
  italic: string;
  strike: string;
  divider: string;
  horizontalRule: string;
  color: string;
  blockquote: string;
  bulletList: string;
  numberedList: string;
  table: string;
  fixTables: string;
  deleteTable: string;
  insertTable: string;
  codeBlock: string;
  code: string;
  redo: string;
  undo: string;
  presentation: string;
  placeholder: string;
  link: string;
  ardoqLink: string;
  image: string;
};

export const defaultToolbarItemLabels: ToolbarItemLabels = {
  textStyles: 'Text Styles',
  normal: 'Normal',
  heading: (level: number) => `Heading ${level}`,
  bold: 'Bold',
  italic: 'Italic',
  strike: 'Strike',
  divider: 'Divider',
  horizontalRule: 'Horizontal Rule',
  color: 'Color',
  blockquote: 'Blockquote',
  bulletList: 'Bullet List',
  numberedList: 'Numbered List',
  table: 'Table',
  fixTables: 'Fix tables',
  deleteTable: 'Delete table',
  insertTable: 'Insert table',
  codeBlock: 'Code Block',
  code: 'Code',
  redo: 'Redo',
  undo: 'Undo',
  presentation: 'Presentation',
  placeholder: 'Placeholder',
  link: 'Link',
  ardoqLink: 'Ardoq Link',
  image: 'Image',
};

export const createToolbarActionItemToToolbarDropdownOption =
  (config: Record<string, ToolbarItem>) =>
  (actionName: string): ToolbarDropdownOption => {
    const item = config[actionName];
    if (item.type !== 'action') {
      throw new Error(`Toolbar item ${actionName} needs to be an action item`);
    }
    return {
      name: item.label,
      label: item.label,
      isActive: (editor: Editor) => editor.isActive(actionName),
      onClick: (editor: Editor) => item.onClick(editor),
      shortcut: item.shortcut,
    };
  };

export const createToolbarItemsConfig = (
  labels: ToolbarItemLabels = defaultToolbarItemLabels
): Record<string, ToolbarItem> => ({
  paragraph: {
    type: 'dropdown',
    label: labels.textStyles,
    tooltipText: labels.textStyles,
    icon: {
      name: IconName.FORMAT_SIZE,
    },
    getOptions(editor) {
      const headingExtension = findExtensionByName(editor, 'heading');
      const headingOptions =
        headingExtension &&
        editor
          .can()
          .toggleHeading({ level: headingExtension.options.levels[0] })
          ? headingExtension.options.levels.map((level: Level) => {
              return {
                label: labels.heading(level),
                onClick: (editor: Editor) =>
                  editor.chain().focus().toggleHeading({ level }).run(),
                isActive: (editor: Editor) =>
                  editor.isActive('heading', { level }),
                shortcut: ['mod', 'alt', `${level}`],
              };
            })
          : [];

      return [
        {
          label: labels.normal,
          onClick: (editor: Editor) =>
            editor.chain().focus().setParagraph().run(),
          isActive: (editor: Editor) => editor.isActive('paragraph'),
          shortcut: ['mod', 'alt', '0'],
        },
        ...headingOptions,
      ];
    },
  },
  bold: {
    type: 'action',
    tooltipText: labels.bold,
    icon: {
      name: IconName.FORMAT_BOLD,
    },
    label: labels.bold,
    onClick: (editor: Editor) => editor.chain().focus().toggleBold().run(),
    shortcut: ['mod', 'B'],
  },
  italic: {
    type: 'action',
    tooltipText: labels.italic,
    icon: {
      name: IconName.FORMAT_ITALIC,
    },
    label: labels.italic,
    onClick: (editor: Editor) => editor.chain().focus().toggleItalic().run(),
    shortcut: ['mod', 'I'],
  },
  strike: {
    type: 'action',
    tooltipText: labels.strike,
    icon: {
      name: IconName.STRIKETHROUGH,
    },
    label: labels.strike,
    onClick: (editor: Editor) => editor.chain().focus().toggleStrike().run(),
    shortcut: ['mod', 'shift', 'S'],
  },
  horizontalRule: {
    type: 'action',
    tooltipText: labels.divider,
    icon: {
      name: IconName.REMOVE,
    },
    label: labels.horizontalRule,
    onClick: (editor: Editor) =>
      editor.chain().focus().setHorizontalRule().run(),
  },
  color: {
    type: 'color-picker',
    tooltipText: labels.color,
    resolveCurrentColor: (editor: Editor) => {
      return editor.getAttributes('textStyle').color || '#000000';
    },
    icon: {
      name: IconName.TEXT_FORMAT,
    },
    label: labels.color,
    onClick: (editor: Editor, color: string) =>
      editor.chain().focus().setColor(color).run(),
  },
  blockquote: {
    type: 'action',
    tooltipText: labels.blockquote,
    icon: {
      name: IconName.FORMAT_QUOTE,
    },
    label: labels.blockquote,
    onClick: (editor: Editor) =>
      editor.chain().focus().toggleBlockquote().run(),
    shortcut: ['mod', 'shift', 'B'],
  },
  bulletList: {
    type: 'action',
    tooltipText: labels.bulletList,
    icon: {
      name: IconName.FORMAT_LIST_BULLETED,
    },
    label: labels.bulletList,
    onClick: (editor: Editor) =>
      editor.chain().focus().toggleBulletList().run(),
    shortcut: ['mod', 'shift', '8'],
  },
  orderedList: {
    type: 'action',
    tooltipText: labels.numberedList,
    icon: {
      name: IconName.FORMAT_LIST_NUMBERED,
    },
    label: labels.numberedList,
    onClick: (editor: Editor) =>
      editor.chain().focus().toggleOrderedList().run(),
    shortcut: ['mod', 'shift', '7'],
  },
  table: {
    type: 'dropdown',
    tooltipText: labels.table,
    icon: {
      name: IconName.TABLE_CHART,
    },
    label: labels.table,
    isDisabled: (editor: Editor) =>
      // We currently don't support tables in lists
      editor.isActive('bulletList') || editor.isActive('orderedList'),
    getActiveOptions: (_: Editor) => [
      {
        name: 'table',
        label: labels.fixTables,
        onClick: (editor: Editor) => editor.chain().focus().fixTables().run(),
      },
      {
        name: 'table',
        label: labels.deleteTable,
        buttonType: DropdownButtonOptionType.DANGER,
        onClick: (editor: Editor) => editor.chain().focus().deleteTable().run(),
      },
    ],
    getOptions: (_: Editor) => [
      {
        name: 'table',
        label: labels.insertTable,
        onClick: (editor: Editor) =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run(),
      },
    ],
  },
  codeBlock: {
    type: 'action',
    tooltipText: labels.codeBlock,
    icon: {
      name: IconName.CODE_BLOCK,
    },
    label: labels.codeBlock,
    shortcut: ['mod', 'alt', 'C'],
    onClick: (editor: Editor) => editor.chain().focus().setCodeBlock().run(),
  },
  code: {
    type: 'action',
    tooltipText: labels.code,
    icon: {
      name: IconName.CODE,
    },
    label: labels.code,
    onClick: (editor: Editor) => editor.chain().focus().toggleCode().run(),
    shortcut: ['mod', 'E'],
  },
  redo: {
    type: 'action',
    tooltipText: labels.redo,
    icon: {
      name: IconName.REDO,
    },
    label: labels.redo,
    onClick: (editor: Editor) => editor.chain().focus().redo().run(),
    shortcut: ['mod', 'shift', 'Z'],
  },
  undo: {
    type: 'action',
    tooltipText: labels.undo,
    icon: {
      name: IconName.UNDO,
    },
    label: labels.undo,
    onClick: (editor: Editor) => editor.chain().focus().undo().run(),
    shortcut: ['mod', 'Z'],
  },

  presentation: {
    type: 'action',
    tooltipText: labels.presentation,
    icon: {
      name: IconName.PRESENTATION,
    },
    label: labels.presentation,
    onClick: async (editor: Editor) => {
      handlePresentationSelection(editor);
    },
  },
  placeholderDropdown: {
    type: 'dropdown',
    tooltipText: labels.placeholder,
    icon: {
      name: IconName.BROADCAST_PLACEHOLDER,
    },
    label: labels.placeholder,
    getOptions: (editor: Editor) => {
      const items =
        (editor.extensionStorage.placeholderDropdown
          .items as PlaceholderExtensionItems[]) || [];
      return items.map(item => {
        return {
          name: 'placeholder',
          label: item.label,
          onClick: (editor: Editor) => {
            editor.commands.insertPlaceholder(item);
          },
        };
      });
    },
  },
  link: {
    type: 'link-dropdown',
    tooltipText: labels.link,
    icon: {
      name: IconName.REFERENCE,
    },
    label: labels.link,
  },
  internalLink: {
    type: 'action',
    tooltipText: labels.ardoqLink,
    icon: {
      name: IconName.REFERENCE_TYPE,
    },
    label: labels.ardoqLink,
    onClick: async (editor: Editor) => {
      if (editor.isActive('internalLink')) {
        editor.commands.removeInternalLink();
        return;
      }
      const linkPicker = editor.extensionStorage.internalLink.linkPicker;
      if (!linkPicker) return;
      const result = await linkPicker();
      if (!result) return;
      editor.commands.insertLinks(result);
    },
  },
  image: {
    type: 'action',
    tooltipText: labels.image,
    icon: {
      name: IconName.COLLECTIONS,
    },
    label: labels.image,
    onClick: async (editor: Editor) => {
      const imagePicker = editor.extensionStorage.image.imagePicker;
      if (!imagePicker) return;
      const result = await imagePicker();
      if (!result) return;
      editor.commands.insertImages(result.map(withDefaultSize));
    },
  },
});
